import * as THREE from "three";
import { HqMaterials } from "../hqMaterials";
import { groundAt } from "../terrain";
import { Heading, LevelSim, SimConstants, SimEventKind, Tower, findTarget, isBarracks, isBeam, isHex, isMortar } from "@highmarch/domain";
import { VFX } from "../../../../../art/vfx.js";
import { cellToWorld } from "../scene";
import { hqNode, lib } from "./common";
import { buildTowerModel } from "./towerModel";
import { SkinKits } from "../skins/kit/runtime";
import { TowerOcclusion } from "./occlusion";
import { SeeThrough } from "../seeThrough";
import { muzzleWorld } from "./towerMuzzle";
import { RigidRig } from "../rigidRig";
import { TowerLod } from "./towerLod";
import { EnemyViews } from "./enemies";
import { BaseSkin } from "./baseSkin";
import { TowerFeel } from "./towerFeel";
import { Settings, reducedVfx } from "../../ui/settings";
import { FortressCompanion } from "./fortressCompanion";
import { FortressAmbience } from "./fortressAmbience";
import { FortressDamage } from "./fortressDamage";
import { TowerView } from "./towerView";
import { BRAZIER_FLARE, BREATH_AT, HIDDEN_SCALE, LAND_KEYS, LAND_SECONDS, ORBITING_WEAPONS, SOOT_CHANCE, SOOT_LIFT, braziers, chargeGlow, crownTick, feel, idle, keepCentre, landSquash, measureBraziers, modelParts, nock, orbitOf, poseTowerWeapon, poseWeapon, reveal, towerColour, baseShot, shotFeel, FIRE_RESTART_AT } from "./towerMotion";
import { FortressRhythm, strikeLead, towerFireRate } from "./towerRhythm";
import { SkinFx } from "../skins/kit/fx";
import { TOWER_SHOT, releaseSeconds } from "./shotTable";
import { roundFlight } from "../vfxRouting";
import { phoenixRebirth, tier4Clips } from "./tier4Clips";
import { aimSpecOf, aimState, releaseDelay, stepAim } from "./towerAim";
import type { DrawnFeet } from "../vfxRouting";
import { PennantFacing } from "./pennantFacing";

export type { WeaponPose } from "./towerView";
export { FortressRhythm, strikeLead, towerFireRate } from "./towerRhythm";

/**
 * Towers on the map. HQ models play their baked clips (`build` on placement and upgrade, `fire` on each shot, `idle`
 * otherwise) and yaw their weapon node toward the last target; the plain blocks (no library) recoil their turret.
 *
 * On top of the clips (art_review/towers.md F8, F9; vfx.md F11): every shot squashes the whole tower and flashes its
 * glow, heavy towers kick back and puff dust, a placed tower drops the last half metre in, lightning towers arc
 * between their coils and fire towers shed embers while idle. The fortress wears the equipped skin and, when the
 * skin has one, plays its `show` reveal on level start instead of `build`.
 */
export class TowerViews {
  readonly group = new THREE.Group();
  private readonly active = new Map<number, TowerView>();
  /** Set by the controller: fired when a build sequence starts, so the effects layer can add dust and sparkles. */
  onBuild: ((cell: { x: number; y: number }, upgrade: boolean) => void) | null = null;
  /** Set by the controller: the sprite VFX once loaded (shot dust, idle arcs and embers). */
  fx: (() => VFX | null) | null = null;
  private readonly occlusion = new TowerOcclusion();
  /** False until the first tick: towers present then (the fortress, staged towers) are not "placed" and skip the drop. */
  private started = false;
  private readonly tmpA = new THREE.Vector3();
  private readonly tmpB = new THREE.Vector3();
  private readonly tmpM = new THREE.Matrix4();
  /** Seconds of view time, for the braziers' flicker. */
  private clock = 0;
  /** Zoomed out past the grunts' LOD distance: the models draw their far detail (towerLod.ts). */
  private far = false;
  /** Set by the level: where an enemy is drawn (its feet), so a weapon tracks what the player sees, not the sim's tick. */
  enemyFeet: DrawnFeet | null = null;
  constructor(private readonly sim: LevelSim) {}

  /** Seconds this tick's shot by `towerId` waits for its weapon to come round (views/towerAim.ts): the VFX router delays its round by it. */
  aimDelay(towerId: number): number { return this.active.get(towerId)?.aimDelay ?? 0; }

  /** Which way a tower's weapon points (world, unit), read at the round's release: the round leaves along it. */
  muzzleDir(towerId: number): THREE.Vector3 | null {
    const v = this.active.get(towerId);
    const node = v?.model.muzzle?.node;
    if (!v?.aim || !node) return null;
    node.updateWorldMatrix(true, false);
    return new THREE.Vector3(0, 0, -1).transformDirection(node.matrixWorld);
  }

  /**
   * HOOK for the `look` agent's per-object glow flash (hqMaterials.ts). Set at merge to their API, e.g.
   * `TowerViews.glowFlash = (root, gain, s) => HqMaterials.flashGlow(root, gain, s)`; until then shots don't flash.
   */
  static glowFlash: ((root: THREE.Object3D, gain: number, seconds: number) => void) | null = (root, gain, seconds) => HqMaterials.flashGlow(root, gain, seconds);

  /** Set by the controller: the base has started its `seconds`-long `charge` at this conductor tip (VFX wind-up). */
  onBaseCharge: ((tip: THREE.Vector3, keep: THREE.Vector3, seconds: number) => void) | null = null;
  /** A `fire` clip may restart once it is this far through (towerMotion.ts shotFeel). */
  static readonly FireRestartAt = FIRE_RESTART_AT;

  /**
   * World position of a named node on a tower's model (`gun_tip`, `bore`, `condgem0` …), read after the rig update.
   * The tower's muzzle part answers with its measured muzzle (views/towerMuzzle.ts): the barrel's mouth, not the
   * part's origin, at the current recoil and aim.
   */
  nodeWorld(towerId: number, suffix: string): THREE.Vector3 | null {
    const v = this.active.get(towerId);
    if (!v) return null;
    if (v.model.muzzle && suffix === v.model.muzzlePart) return muzzleWorld(v.model.muzzle);
    const node = hqNode(v.model.storeys, v.model.name, suffix);
    if (!node) return null;
    return node.getWorldPosition(new THREE.Vector3());
  }

  /** The world box a tower's model fills at rest (null if it has no view yet), for picking it where it is drawn. */
  restBox(towerId: number): THREE.Box3 | null { return this.active.get(towerId)?.box ?? null; }

  /** Plays every standing tower's clips `seconds` on: towers staged all at once (the title's diorama) skip their build. */
  settle(seconds: number): void { for (const v of this.active.values()) v.model.rig.update(seconds); }

  /** Fades any tower that hides a live enemy from the camera (see TowerOcclusion). */
  fadeOccluders(dt: number, camera: THREE.Camera): void {
    const targets: THREE.Vector3[] = [];
    for (const e of this.sim.enemySlots) if (e.alive) targets.push(new THREE.Vector3(e.x.toNumber(), 0.12, e.y.toNumber()));
    // and the build plots while a tower is being placed (seeThrough.ts thins the props; a tower ghosts whole)
    for (const p of SeeThrough.targets()) targets.push(p);
    // The fortress never ghosts: it is the thing being defended, and its walls hide nothing the player needs to tap.
    // Nor does a Hex Totem: a thin pole that hides next to nothing, whose cursed raiders crowd round it all the time —
    // ghosted, the dark carved pole all but vanished from its own ward.
    const towers = [...this.active.entries()].filter(([id]) => { const t = this.sim.findTower(id); if (!t) return true; const a = this.sim.balance.towers[t.archetype]; return !a.isBase && !isHex(a); }).map(([, v]) => v);
    this.occlusion.update(dt, camera, towers, targets);
  }

  /** Leaving the level: frees every tower clone's per-instance bone textures (a sold tower frees its own at once). */
  dispose(): void {
    for (const v of this.active.values()) { v.companion?.dispose(); RigidRig.dispose(v.model.storeys); }
    this.active.clear();
    this.group.clear();
  }

  onTick(): void {
    // A Move card (M55): the tower's view is dropped and built afresh on its new square, so it lands there as a placed
    // tower does (the drop, then the construction sequence) instead of sliding across the map.
    for (const ev of this.sim.events.all()) if (ev.kind === SimEventKind.TowerMoved) this.forget(ev.a);
    for (const t of this.sim.towerSlots) {
      if (!t.alive) continue;
      let v = this.active.get(t.id);
      if (!v) { v = this.build(t); this.active.set(t.id, v); }
      if (v.tier !== t.tier) {
        // Tier-up: the ascension (squash → swap under a flash → rise) when the VFX is up; otherwise swap and build.
        if (this.fx?.() && !reducedVfx() && !v.isBase) { v.tier = t.tier; v.asc = { t: 0, swapped: false }; this.ascendStart(v); }
        else this.rebuild(t, v, true);
      }
    }
    this.started = true;
    for (const ev of this.sim.events.all()) {
      if (ev.kind === SimEventKind.TroopSpawned || ev.kind === SimEventKind.BeamStarted || ev.kind === SimEventKind.EnemyHexed) { this.towersV2(ev.kind, ev.a, ev.b); continue; }
      if (ev.kind === SimEventKind.PhoenixRisen && ev.b === 1) { phoenixRebirth(this.active.get(ev.a)); continue; }
      if (ev.kind !== SimEventKind.TowerFired) continue;
      const v = this.active.get(ev.a);
      // an Arcane Spire's TowerFired is a pulse of the beam it holds, not a shot: its clips follow the beam (update)
      if (v && v.special === "beam") continue;
      // a Mortar aims where its shell will land (the sim leads the target along the road), not at the target
      const shell = v && v.special === "mortar" ? this.shellOf(ev.a) : null;
      const target = shell ?? this.sim.findEnemy(ev.b);
      // the fortress settles strike or zap on every shot, a killing one too (its target is gone by now)
      if (v?.isBase && !target) { baseShot(v, TowerViews.glowFlash); continue; }
      if (!v || !target) continue;
      v.flash = 0.12;
      const tx = target.x.toNumber(), tz = target.y.toNumber();
      const dir = new THREE.Vector3(tx, 0, tz).sub(v.root.position).setY(0);
      const t = this.sim.findTower(ev.a);
      const rate = t ? towerFireRate(this.sim, t) : 1;
      v.aimDelay = 0;
      if (v.aim && v.aimSpec) {
        // the weapon was tracking (update); this shot turns it urgently, and its visuals wait a moment if need be
        const bearing = Math.atan2(dir.x, dir.z);
        v.aimTarget = ev.b;
        if (shell) { v.aimPoint = { x: tx, z: tz }; v.aimHold = TowerViews.ShellHold; }
        v.aim.urgent = true;
        const shot = TOWER_SHOT[v.arch];
        // a Mortar's shell flies on the sim's clock (mortarShells.ts), so it never waits
        if (shot && !shell && !v.isBase) v.aimDelay = releaseDelay(v.aimSpec, v.aim.yaw.value, v.aim.yaw.vel, bearing, releaseSeconds(shot, rate), v.aim.yaw);
      } else if (dir.lengthSq() > 0.001) {
        v.yaw = Math.atan2(dir.x, dir.z);
        v.model.turret.rotation.y = v.yaw;
      }
      if (v.isBase) { baseShot(v, TowerViews.glowFlash); continue; }
      if (v.aimDelay > 0) { v.pendingShot = { in: v.aimDelay, dir, rate, x: tx, z: tz }; continue; }
      v.pendingShot = null;
      shotFeel(v, dir, rate, this.fx?.() ?? null);
      this.flourish(v, tx, tz, rate);
    }
    for (const id of [...this.active.keys()]) if (this.sim.findTower(id) === null) this.forget(id);
  }

  /** Drops a tower's view (sold, cleared, or moved: a moved one is built again where it now stands). */
  private forget(id: number): void {
    const v = this.active.get(id);
    if (!v) return;
    v.companion?.dispose(); this.group.remove(v.root); this.occlusion.forget(v.root); RigidRig.dispose(v.model.storeys); this.active.delete(id);
  }

  update(dt: number): void {
    const fx = reducedVfx() ? null : this.fx?.() ?? null;
    this.clock += dt;
    const far = EnemyViews.lodFar();
    if (far !== this.far) { this.far = far; for (const v of this.active.values()) TowerLod.setFar(v.model.storeys, far); }
    for (const [id, v] of this.active) {
      const tw = this.sim.findTower(id);
      if (v.aim && tw) this.aimUpdate(tw, v, dt);
      if (v.pendingShot) {
        const p = v.pendingShot;
        p.in -= dt;
        if (p.in <= 0) { v.pendingShot = null; shotFeel(v, p.dir, p.rate, this.fx?.() ?? null); this.flourish(v, p.x, p.z, p.rate); }
      }
      poseTowerWeapon(v, dt);
      if (v.glowIn <= dt) { v.glowIn = Infinity; TowerViews.glowFlash?.(v.model.storeys, TowerFeel.GlowGain, TowerFeel.GlowSeconds); }
      else v.glowIn -= dt;
      v.damage?.update(FortressDamage.stageOf(this.sim.baseHp, this.sim.maxBaseHp), dt, fx);
      if (v.flash > 0) { v.flash -= dt; if (!v.model.weapon) v.model.turret.position.y = v.model.restY + 0.08 * Math.max(0, v.flash / 0.12); }
      v.fireAt += dt;
      if (v.fireAt < v.fireLen) nock(v);
      // Disabled (bombed) towers droop their weapon and sink a little until they recover.
      const t = this.sim.findTower(id);
      const disabled = t !== null && t.disabledTicksLeft > 0;
      if (t && this.sim.balance.towers[t.archetype].isBase) this.windUp(t, v);
      if (v.model.weapon) v.model.weapon.rotation.x += disabled ? -0.7 : 0;
      else v.model.turret.rotation.x = disabled ? -0.7 : 0;
      v.root.scale.y = disabled ? 0.88 : 1;
      if (t && v.special) this.specialUpdate(t, v, dt);
      if (t && t.tier > 3) tier4Clips(this.sim, t, v);
      if (v.asc) this.ascend(id, v, dt);
      else feel(v, dt);
      if (v.phase === "charging") chargeGlow(v, dt, TowerViews.glowFlash);
      if (v.revealAt < Infinity) reveal(v, dt, fx, this.sim.grid.baseHeading as Heading);
      if (v.braziers) braziers(v, fx, this.clock);
      v.companion?.update(dt, fx);
      v.ambience?.update(dt, fx);
      if (fx) { idle(v, dt, fx); if (v.tier >= 3 && !v.asc) crownTick(v, dt, fx); if (!v.isBase) SkinFx.idle(v.model.storeys, dt, fx); else SkinFx.fortressIdle(v.model.storeys, v.model.name, dt, fx); }
      if (!v.isBase) SkinKits.chargeTick(v.model.storeys, dt);          // a big kit's attack-synced glow (kit/charge.ts)
    }
  }

  // ---- TOWERS_V2 (docs/TOWERS_V2.md): the clips of the four new towers follow their sim state, not TowerFired

  /** The shell `towerId` launched this tick (its landing point), or null. */
  private shellOf(towerId: number): { x: { toNumber(): number }; y: { toNumber(): number } } | null {
    for (const ev of this.sim.events.all()) if (ev.kind === SimEventKind.ShellLaunched && ev.b === towerId) return this.sim.shells.find((s) => s.id === ev.a) ?? null;
    return null;
  }

  /**
   * A Barracks' soldiers muster (TroopSpawned of a soldier it owns): its `deploy` clip opens the gate and snaps the
   * banners. A spire locks on (BeamStarted): `fire` flares the crystal, then `channel` holds while the beam does. A
   * totem curses someone new (EnemyHexed): its `fire` pulse, at most once a clip.
   */
  private towersV2(kind: SimEventKind, a: number, b: number): void {
    if (kind === SimEventKind.TroopSpawned) {
      const troop = this.sim.troops.find(a);
      const v = troop && troop.ownerTowerId !== 0 ? this.active.get(troop.ownerTowerId) : undefined;
      // not over the `build` (the first muster comes with the placement), nor twice inside one gate's opening
      if (v && v.special === "barracks" && !v.asc && (!v.model.rig.busy || (v.fireAt !== Infinity && v.fireAt > TowerViews.DeployHold))) {
        if (v.model.rig.playOnce("deploy", false, null, { solo: true }) > 0) { v.fireAt = 0; v.fireLen = TowerViews.DeployHold; }
        const post = this.sim.squads.find((s) => s.towerId === troop?.ownerTowerId)?.post;
        if (post) this.skinFlourishAt(v, post.x, post.y);
      }
      return;
    }
    const v = this.active.get(kind === SimEventKind.EnemyHexed ? b : a);
    if (!v || v.asc) return;
    if (kind === SimEventKind.BeamStarted && v.special === "beam") {
      v.model.rig.playOnce("fire", false, null, { solo: false });
      const e = this.sim.findEnemy(b);
      if (e) this.skinFlourishAt(v, e.x.toNumber(), e.y.toNumber());
      v.glowIn = TowerViews.ArcaneRelease;
      return;
    }
    if (kind === SimEventKind.EnemyHexed && v.special === "hex" && !v.model.rig.busy) {
      v.model.rig.playOnce("fire");
      const e = this.sim.findEnemy(a);
      if (e) this.skinFlourishAt(v, e.x.toNumber(), e.y.toNumber());
      v.glowIn = TowerViews.HexRelease;
    }
  }

  /** Per frame for a TOWERS_V2 tower: the spire's channel loop on its ramp, the Barracks turning its gate to its post. */
  private specialUpdate(t: Tower, v: TowerView, dt: number): void {
    if (v.special === "beam") {
      const holding = t.beamTarget !== 0 && t.disabledTicksLeft <= 0;
      if (holding) v.model.rig.playLoop("channel", TowerViews.channelRate(this.sim.beamRamp(t).toNumber()));
      else v.model.rig.playLoop("idle");
    } else if (v.special === "barracks") {
      const q = this.sim.squads.find((s) => s.towerId === t.id);
      if (q?.post) v.face = TowerViews.gateYaw(t.cell, q.post);
      const r = v.model.storeys.rotation;
      const d = Math.atan2(Math.sin(v.face - r.y), Math.cos(v.face - r.y));
      r.y += Math.abs(d) < 1e-3 ? d : d * Math.min(1, dt * 6);
    }
  }

  /** The `channel` clip's speed at ramp `r` (0–1): the rings tighten faster as the beam charges. */
  static channelRate(r: number): number { return 0.7 + 1.3 * Math.max(0, Math.min(1, r)); }

  /**
   * The yaw that turns a Barracks' gate (the model's front, +z after HQ_YAW) toward its post, in quarter turns so the
   * guardhouse stays square on its cell. Pure.
   */
  static gateYaw(tower: { x: number; y: number }, post: { x: number; y: number }): number {
    const a = Math.atan2(post.x - tower.x, post.y - tower.y);
    return Math.round(a / (Math.PI / 2)) * (Math.PI / 2);
  }

  /** Seconds a Barracks' `deploy` holds the gate open (f8–f28) before another muster may replay it. */
  static readonly DeployHold = 28 / 30;
  /** The spire's crystal flares on `fire` frame 8 and the totem's eyes on its frame 8 (art hand-off 2026-10-04). */
  static readonly ArcaneRelease = 8 / 30;
  static readonly HexRelease = 8 / 30;

  /** A fire tower's braziers as sprite flames (towerMotion.ts). */
  static readonly braziers = braziers;

  static readonly BrazierFlare = BRAZIER_FLARE;

  // ---- Aim (owner report 2026-10-06): views/towerAim.ts

  /** Seconds a Mortar holds its bearing on its shell's landing point after a shot, before it tracks again. */
  static readonly ShellHold = 0.9;
  /** How often (s) a weapon looks again for what the sim will shoot next (findTarget, read-only). */
  static readonly RetargetSeconds = 0.1;
  /** The longest lead (s) a weapon aims ahead of a moving target. */
  static readonly MaxLead = 0.6;

  /** The bearing from a tower to the nearest road cell (where a new tower first looks). */
  private roadBearing(t: Tower): number {
    let best = Infinity, yaw = 0;
    for (const c of this.sim.road.roadCells) {
      const d = (c.x - t.cell.x) ** 2 + (c.y - t.cell.y) ** 2;
      if (d < best && d > 0) { best = d; yaw = Math.atan2(c.x - t.cell.x, c.y - t.cell.y); }
    }
    return yaw;
  }

  /**
   * One frame of a weapon's aim: who it tracks (the enemy it last shot while that one stays in reach, else whoever the
   * sim's own targeting would pick now — read only, the sim is not touched), where that enemy is drawn plus a lead
   * for its walk, and the spring toward that bearing (towerAim.ts stepAim). A bombed tower lets its weapon droop.
   */
  private aimUpdate(t: Tower, v: TowerView, dt: number): void {
    const a = v.aim!, spec = v.aimSpec!;
    const disabled = t.disabledTicksLeft > 0;
    v.retargetIn -= dt;
    if (v.retargetIn <= 0) {
      v.retargetIn = TowerViews.RetargetSeconds;
      const range = this.sim.rangeOf(t).toNumber() + 0.5;
      const kept = v.aimTarget ? this.sim.findEnemy(v.aimTarget) : null;
      const inReach = kept && Math.hypot(kept.x.toNumber() - t.cell.x, kept.y.toNumber() - t.cell.y) <= range;
      v.aimTarget = disabled ? 0 : inReach ? kept!.id : findTarget(this.sim, t)?.id ?? 0;
    }
    v.aimHold -= dt;
    let at: { x: number; z: number } | null = null;
    if (v.aimPoint && v.aimHold > 0) at = v.aimPoint;
    else if (v.aimTarget && !disabled) {
      v.aimPoint = null;
      const feet = this.enemyFeet?.(v.aimTarget) ?? null;
      const e = feet ? null : this.sim.findEnemy(v.aimTarget);
      const x = feet ? feet.x : e ? e.x.toNumber() : NaN, z = feet ? feet.z : e ? e.y.toNumber() : NaN;
      if (Number.isFinite(x)) {
        // the target's drawn velocity, smoothed (a new target starts from rest)
        if (v.leadId !== v.aimTarget || !v.leadAt) { v.leadId = v.aimTarget; v.leadX = 0; v.leadZ = 0; v.leadAt = { x, z }; }
        else if (dt > 0) {
          const k = Math.min(1, dt / 0.25);
          v.leadX += ((x - v.leadAt.x) / dt - v.leadX) * k; v.leadZ += ((z - v.leadAt.z) / dt - v.leadZ) * k;
          v.leadAt.x = x; v.leadAt.z = z;
        }
        const shot = TOWER_SHOT[v.arch];
        const dist = Math.hypot(x - v.root.position.x, z - v.root.position.z);
        const lead = Math.min(TowerViews.MaxLead, shot ? releaseSeconds(shot, v.fireRate) + dist / shot.speed : 0.2);
        at = { x: x + v.leadX * lead, z: z + v.leadZ * lead };
      }
    }
    const bearing = at ? Math.atan2(at.x - v.root.position.x, at.z - v.root.position.z) : null;
    const cells = at ? Math.hypot(at.x - v.root.position.x, at.z - v.root.position.z) : 0;
    const r = stepAim(a, spec, bearing, cells, dt);
    v.yaw = r.yaw;
    // the gun's elevation (towerAim.ts pitchFor): up is a positive turn about +x for a front at −z
    v.tilt = (v.tilt ?? new THREE.Quaternion()).setFromAxisAngle(TowerViews.X, r.pitch);
  }
  private static readonly X = new THREE.Vector3(1, 0, 0);

  // ---- Tier-3 Ascension (ART_WOW_PASS Phase 2 §2), in code: no `upgrade` clip exists yet.
  private ascendStart(v: TowerView): void {
    const fx = this.fx?.();
    const top = this.tmpA.copy(v.root.position); top.y = v.box.max.y;
    fx?.ascendCharge(this.tmpB.copy(v.root.position), top, towerColour(v.arch), TowerFeel.AscendSquash);
    TowerViews.glowFlash?.(v.model.storeys, 2, TowerFeel.AscendSquash);
  }

  private ascend(id: number, v: TowerView, dt: number): void {
    const a = v.asc!, s = v.model.storeys;
    a.t += dt;
    if (!a.swapped) {
      const g = TowerFeel.ascendGather(a.t);
      s.scale.set(g.xz, g.y, g.xz);                                          // the old tower gathers itself
      if (a.t < TowerFeel.AscendSquash) return;
      const t = this.sim.findTower(id);
      if (!t) { v.asc = null; return; }
      this.rebuild(t, v, true, true); poseTowerWeapon(v, 0);                   // the new weapon aimed from its first frame
      a.swapped = true; a.t = 0;
      const h = v.box.max.y - v.root.position.y;
      const fx = this.fx?.();
      fx?.ascendBurst(this.tmpA.copy(v.root.position), h, towerColour(v.arch), t.tier);
      fx?.towerLevelUp(this.tmpA.copy(v.root.position), h, { color: towerColour(v.arch) });
      TowerViews.glowFlash?.(v.model.storeys, 3, 0.9);                        // emissives 3× → 1× as it settles
    }
    const r = TowerFeel.ascendRise(a.t);                                      // rises out of the flash, overshoots, settles
    s.scale.set(r.xz, r.y, r.xz); s.position.set(0, 0, 0);
    if (a.t >= TowerFeel.AscendRise) { s.scale.set(1, 1, 1); v.asc = null; }
  }

  static readonly BreathAt = BREATH_AT;

  /** A placed tower's landing squash (towerMotion.ts). */
  static readonly LandKeys = LAND_KEYS;
  static readonly LandSeconds = LAND_SECONDS;
  static readonly landSquash = landSquash;

  /** The clips' pose, then the aim on top (towerMotion.ts). */
  static readonly poseWeapon = poseWeapon;

  static readonly HiddenScale = HIDDEN_SCALE;
  static readonly OrbitingWeapons = ORBITING_WEAPONS;

  static readonly SootChance = SOOT_CHANCE;
  static readonly SootLift = SOOT_LIFT;

  /**
   * The fortress winds up (FortressRhythm): once a strike's `fire` has played and an enemy is within reach, `charge`
   * runs — to the sim's first shot at least MinChargeSeconds off when the enemy is already in range, so the clip ends
   * on that shot, or for ChargeSeconds while it is still approaching. A ready fortress holds its charged glow while an
   * enemy is near. Nothing here changes when the sim shoots.
   */
  private windUp(t: Tower, v: TowerView): void {
    if (v.phase === "charging") return;                     // chargeGlow runs it out
    const range = this.sim.rangeOf(t).toNumber();
    let near = Infinity;
    for (const e of this.sim.enemySlots) if (e.alive) near = Math.min(near, Math.hypot(e.x.toNumber() - t.cell.x, e.y.toNumber() - t.cell.y));
    if (near > range + FortressRhythm.ApproachCells) return;
    if (v.phase === "ready") { TowerViews.glowFlash?.(v.model.storeys, FortressRhythm.ChargedGlow, FortressRhythm.GlowHoldSeconds); return; }
    if (v.model.rig.busy) return;                           // the strike's `fire` (or `build`, `show`) plays out first
    const secondsPerTick = 1 / (SimConstants.TicksPerSecond * Math.max(1, Settings.current.speed));
    // the sim fires when the cooldown has counted down to 0 (and a bombed tower after its stun): one tick after
    const next = t.disabledTicksLeft + t.cooldown + 1, period = this.sim.fireIntervalOf(t) + 1;
    v.strikeAt = near <= range ? strikeLead(next, period, secondsPerTick) : FortressRhythm.ChargeSeconds;
    v.chargeLen = v.strikeAt + FortressRhythm.Overlap;
    const clip = lib()?.clipsOf(`hq:${v.model.name}`).find((c) => c.name === "charge")?.duration ?? 0;
    v.model.rig.playOnce("charge", false, null, { timeScale: clip > 0 ? clip / v.chargeLen : 1 });
    v.phase = "charging";
    v.fireAt = Infinity;
    v.chargeAt = 0;
    const tip = this.nodeWorld(t.id, "condgem0") ?? v.root.position.clone().add(new THREE.Vector3(0, v.box.max.y - v.root.position.y, 0));
    this.onBaseCharge?.(tip, keepCentre(v, new THREE.Vector3()), v.chargeLen);
  }

  /**
   * The worn skin's attack flourish (render/skins/kit/fx.ts): a glint at the muzzle on the clip's release and the
   * skin's impact where the round lands, timed like the round itself (vfxRouter.ts): release, then the flight.
   */
  private flourish(v: TowerView, x: number, z: number, rate: number): void {
    const shot = TOWER_SHOT[v.arch];
    const release = shot ? releaseSeconds(shot, rate) : 0;
    SkinKits.chargeShot(v.model.storeys, release);
    const fx = reducedVfx() ? null : this.fx?.() ?? null;
    if (!fx) return;
    const to = this.tmpA.set(x, groundAt(x, z) + 0.12, z).clone();
    const muzzle = () => (v.model.muzzle ? muzzleWorld(v.model.muzzle) : null);
    const from = muzzle() ?? v.root.position;
    const land = shot ? release + roundFlight(from, [to], shot.speed) : 0;
    SkinFx.flourish(v.model.storeys, fx, muzzle, to, release, land);
  }

  /** A shotless tower's worn-skin flourish (a Barracks muster, a spire's lock-on, a curse): the skin's impact at a point. */
  private skinFlourishAt(v: TowerView, x: number, z: number): void {
    const fx = reducedVfx() ? null : this.fx?.() ?? null;
    if (fx) SkinFx.flourish(v.model.storeys, fx, null, this.tmpA.set(x, groundAt(x, z) + 0.12, z).clone(), 0, 0);
  }

  /** Whether this tick's shot by `towerId` is the fortress's strike (false: a zap, or not the fortress). */
  heroShot(towerId: number): boolean { return this.active.get(towerId)?.hero ?? false; }

  private build(t: Tower): TowerView {
    const root = new THREE.Group();
    root.position.copy(cellToWorld(t.cell, groundAt(t.cell.x, t.cell.y)));
    const arch = this.sim.balance.towers[t.archetype];
    const v: TowerView = {
      root, model: buildTowerModel("archer", false, 1), flash: 0, tier: 0, yaw: 0, box: new THREE.Box3(),
      arch: arch.id, isBase: arch.isBase, restY: root.position.y,
      fireAt: Infinity, fireLen: 0, fireRate: 1, nocked: undefined, weaponPose: null, orbit: null, shotAt: Infinity, kickX: 0, kickZ: 0, landAt: Infinity,
      // a tower placed during play drops in over 120 ms (vfx.md F11); the fortress and pre-staged towers just stand
      dropAt: this.started && !arch.isBase && !reducedVfx() ? 0 : Infinity,
      idleIn: Math.random() * 2, coils: null, flames: null, braziers: null, glowIn: Infinity,
      phase: "spent", chargeAt: Infinity, chargeLen: FortressRhythm.ChargeSeconds, strikeAt: 0, hero: false, asc: null, revealAt: Infinity, breathed: false, crownIn: Math.random() * 0.1, damage: null, companion: null, ambience: null,
      special: isMortar(arch) ? "mortar" : isBarracks(arch) ? "barracks" : isBeam(arch) ? "beam" : isHex(arch) ? "hex" : null, face: 0,
      aim: null, aimSpec: null, aimTarget: 0, aimPoint: null, aimHold: 0, retargetIn: 0, leadX: 0, leadZ: 0, leadId: 0, leadAt: null, tilt: null,
      aimDelay: 0, pendingShot: null,
    };
    root.add(v.model.storeys);
    this.rebuild(t, v, false);
    if (v.dropAt === 0) root.position.y = v.restY + TowerFeel.DropHeight;
    this.group.add(root);
    return v;
  }

  /** Swaps in the model for the tower's tier and plays the construction sequence (base included, on level start). */
  private rebuild(t: Tower, v: TowerView, upgrade: boolean, ascended = false): void {
    const arch = this.sim.balance.towers[t.archetype];
    v.tier = t.tier;
    v.root.remove(v.model.storeys);
    RigidRig.dispose(v.model.storeys);
    // a tower wears its worn kit's own model where one ships (big kits: buildTowerModel's default)
    v.model = buildTowerModel(arch.id, arch.isBase, t.tier, arch.isBase ? BaseSkin.current() : undefined, branchKey(this.sim, t));
    TowerLod.attach(v.model.storeys, this.far);           // shadow proxies and the zoomed-out detail (towerLod.ts)
    v.coils = null; v.flames = null; v.fireAt = Infinity; v.nocked = undefined; v.weaponPose = null; v.glowIn = Infinity;
    v.phase = "spent"; v.chargeAt = Infinity; v.hero = false;           // a new fortress model winds up afresh
    v.damage = arch.isBase ? FortressDamage.of(v.model.storeys, v.model.name) : null;
    // The fortress faces its gate down the road: the model's front (+z after HQ_YAW) turns to the level's heading,
    // which also swings its origin offset over the back row of the six-cell footprint.
    if (arch.isBase) {
      v.model.storeys.rotation.y = [0, Math.PI / 2, Math.PI, -Math.PI / 2][this.sim.grid.baseHeading as Heading];
      PennantFacing.face(v.model.storeys, v.model.name, v.model.storeys.rotation.y);     // its pennant still faces the camera
    }
    if (v.special === "barracks") {                       // the guardhouse's gate faces its post (specialUpdate)
      const q = this.sim.squads.find((s) => s.towerId === t.id);
      if (q?.post) v.face = TowerViews.gateYaw(t.cell, q.post);
      v.model.storeys.rotation.y = v.face;
    }
    // The conductors' `arc*` stubs rest at scale ~0 and show only while `charge` or `fire` plays (art/DEV_HANDOFF.md
    // §3). `build` keys them at 1 on its last frame and AssetLibrary.bakeRestPose makes that frame the rest pose, so
    // the mixer — which puts a part back to its pose from before the first clip that moved it — left every stub
    // standing after each clip: white sparks on every spire, all game. Hidden here, before any clip plays.
    if (arch.isBase) for (const a of modelParts(v, "arc")) a.scale.setScalar(TowerViews.HiddenScale);
    v.root.add(v.model.storeys);
    v.model.turret.rotation.y = v.yaw;
    // a weapon that turns tracks its target smoothly (towerAim.ts); the first model faces the road it guards
    if (v.model.weapon && !arch.isBase && v.special !== "beam" && v.special !== "hex") {
      if (!v.aim) { v.yaw = this.roadBearing(t); v.aim = aimState(v.yaw, t.id * 7919 + 13); }
      v.aimSpec = aimSpecOf(arch.id, v.model.name);
    } else { v.aim = null; v.aimSpec = null; }
    v.tilt = null; v.pendingShot = null;
    v.root.updateMatrixWorld(true);
    v.box.setFromObject(v.model.storeys);                 // at rest, before the build clip moves the parts
    v.braziers = measureBraziers(v);
    v.companion?.dispose();
    v.companion = arch.isBase ? FortressCompanion.of(v.model.storeys, v.model.name, v.box, this.group) : null;
    v.ambience = arch.isBase ? FortressAmbience.of(v) : null;
    v.orbit = orbitOf(v);
    this.occlusion.forget(v.root);
    // A skinned fortress reveals itself with its `show` clip on level start (the Dragonlord uncoiling); upgrades and
    // stock models build as before.
    if (arch.isBase && !upgrade && v.model.rig.has("show")) { v.model.rig.playOnce("show"); v.revealAt = 0; v.breathed = false; return; }
    if (ascended) return;                                   // the ascension's rise replaces the scaffold build
    if (v.model.rig.playOnce("build") > 0) this.onBuild?.(t.cell, upgrade);
  }
}

/** The branch id a tier-IV tower fights as (its model and gold dress, M21), else null. */
function branchKey(sim: LevelSim, t: Tower): string | null {
  if (t.tier < 4) return null;
  const i = sim.ascension.branchOf[t.archetype] ?? -1;
  return i >= 0 ? sim.balance.branches.branches[i].id : null;
}
